// PMReportButton_FillTemplate.jsx
//
// Generates a PM report by stamping each room's PM responses onto a base PDF
// template (fetched once from Firebase Storage and cached in-memory).
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { firestore } from "../../firebase";
import { collectPMChecks, fetchPMChecks, parseISO } from "../../utils/reports/pmData";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import {
  drawReportHeaderLib,
  stampPageNumbersLib,
} from "../../utils/reports/pdfChromeLib";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

const TEMPLATE_URL =
  "https://firebasestorage.googleapis.com/v0/b/siegel-room-status.appspot.com/o/PM%20Sheet%20Base.pdf?alt=media&token=259be1fd-c7b9-404b-aa8d-34c03f9645b9";

// Coordinates for stamping values onto the template page.
const COORDS = {
  header: {
    property: { x: 100, y: 580 },
    date: { x: 510, y: 577 },
    inspector: { x: 100, y: 560 },
    inspector2: { x: 510, y: 560 },
    unitNumber: { x: 100, y: 540 },
  },
  status: {
    READY: { x: 315, y: 704 },
    DOWN: { x: 380, y: 704 },
    OCCUPIED: { x: 445, y: 704 },
  },
  checklistCols: { yesX: 327, noX: 390 },
  checklistRows: {
    orig_pets: 485,
    orig_cleanliness: 450,
    orig_pest_control: 430,
    orig_smoke_detector: 410,
    orig_leaks: 390,
    orig_ac_filter: 370,
    orig_flooring: 350,
    orig_appliances: 330,
    orig_basic_clean: 310,
    orig_bathtub: 290,
    orig_fridge_magnets: 270,
    orig_sprinkler_head: 250,
    orig_sex_trafficking_signage: 230,
    orig_toilet: 210,
    orig_caulking: 190,
    orig_electrical_panel: 170,
    orig_windows: 150,
    orig_window_locks: 130,
    orig_entry_door_lock: 110,
  },
  notes: { x: 500, maxWidth: 200, yOffset: 0, fontSize: 9, lineHeight: 11 },
  petsChoice: { x: 80, yOffset: 2, fontSize: 12 },
};

export default function PMReportButtonFillTemplate({
  clientId,
  selectedProperty,
  rooms,
  currentUserName = "Unknown",
}) {
  const db = firestore;
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [templateError, setTemplateError] = useState("");

  // Cache the template bytes across runs.
  const templateBytesRef = useRef(null);

  // ---------- drawing helpers ----------
  const circleAt = (page, x, y, r = 9, color = rgb(0, 0, 0)) =>
    page.drawEllipse({
      x,
      y,
      xScale: r + 3,
      yScale: r,
      borderWidth: 1.2,
      borderColor: color,
    });

  const drawText = (page, text, x, y, opts) => {
    if (!text) return;
    page.drawText(String(text), {
      x,
      y,
      size: opts?.size ?? 11,
      font: opts?.font,
      color: opts?.color ?? rgb(0, 0, 0),
      maxWidth: opts?.maxWidth,
      lineHeight: opts?.lineHeight ?? 13,
    });
  };

  const displayDateFor = (entry) => {
    if (entry.pmDate) return entry.pmDate.toLocaleDateString();
    const c = entry.pmDoc?.completedAt;
    if (typeof c === "string") {
      const d = parseISO(c);
      return d ? d.toLocaleDateString() : "";
    }
    if (c?.seconds) return new Date(c.seconds * 1000).toLocaleDateString();
    return "";
  };

  const addCoverPage = (pdf, font, boldFont, matches, start, end) => {
    const page = pdf.addPage([612, 792]);
    const left = 60;
    let y = drawReportHeaderLib(page, {
      title: "Preventative Maintenance Report",
      dateRange: { start, end },
      generatedBy: currentUserName,
      font,
      boldFont,
    });
    y -= 6;
    page.drawText("Rooms with PM in range:", { x: left, y, size: 12, font });
    y -= 18;
    for (const m of matches) {
      if (y < 60) {
        pdf.addPage([612, 792]);
        y = 792 - 60;
      }
      page.drawText(`• ${m.roomName} — ${m.pmDate.toLocaleString()}`, { x: left, y, size: 10, font });
      y -= 14;
    }
  };

  const fillTemplatePage = (page, font, entry) => {
    const { header, status, checklistCols, checklistRows, notes } = COORDS;
    const responses = entry.pmDoc?.responses || {};
    const chosenDate = displayDateFor(entry);

    drawText(page, selectedProperty || entry.pmDoc?.propertyId || "", header.property.x, header.property.y, { font, size: 12 });
    drawText(page, chosenDate, header.date.x, header.date.y, { font, size: 12 });
    drawText(page, "Greg Gleason", header.inspector.x, header.inspector.y, { font, size: 12 });
    drawText(page, "Greg Gleason", header.inspector2.x, header.inspector2.y, { font, size: 12 });
    drawText(page, entry.roomName, header.unitNumber.x, header.unitNumber.y, { font, size: 12 });

    circleAt(page, status.OCCUPIED.x, status.OCCUPIED.y, 9);

    Object.entries(checklistRows).forEach(([key, y]) => {
      const r = responses[key];
      if (!r) return;
      const choiceRaw = String(r.choice || "").trim();
      const choice = choiceRaw.toLowerCase();
      const n = (r.notes || "").trim();
      const safeX = Math.min(notes.x, page.getSize().width - 40);

      // Pets is shown as a text label rather than a Yes/No circle.
      if (key === "orig_pets") {
        const label = choiceRaw
          ? choiceRaw[0].toUpperCase() + choiceRaw.slice(1).toLowerCase()
          : "";
        drawText(page, label || "-", COORDS.petsChoice.x, y + COORDS.petsChoice.yOffset, {
          font,
          size: COORDS.petsChoice.fontSize,
        });
        if (n) {
          drawText(page, n, safeX, y + notes.yOffset, {
            font,
            size: notes.fontSize,
            maxWidth: notes.maxWidth,
            lineHeight: notes.lineHeight,
          });
        }
        return;
      }

      if (choice === "yes") circleAt(page, checklistCols.yesX, y + 4, 8.5);
      else if (choice === "no") circleAt(page, checklistCols.noX, y + 4, 8.5);

      if (n) {
        drawText(page, n, safeX, y + notes.yOffset, {
          font,
          size: notes.fontSize,
          maxWidth: notes.maxWidth,
          lineHeight: notes.lineHeight,
        });
      }
    });
  };

  // ---------- template loading ----------
  const ensureTemplateLoaded = async () => {
    if (templateBytesRef.current) return templateBytesRef.current;
    setTemplateError("");
    const res = await fetch(TEMPLATE_URL, { credentials: "omit", cache: "force-cache" });
    if (!res.ok) throw new Error(`Failed to fetch template: ${res.status}`);
    const bytes = await res.arrayBuffer();
    templateBytesRef.current = bytes;
    return bytes;
  };

  // Prefetch the template when the dialog opens so the user doesn't wait at generate time.
  useEffect(() => {
    if (open && !templateBytesRef.current) {
      ensureTemplateLoaded().catch((e) =>
        setTemplateError(e.message || "Failed to load template PDF.")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---------- generate ----------
  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      const templateBytes = await ensureTemplateLoaded();

      const matches = collectPMChecks(rooms, start, end);
      if (!matches.length) {
        alert("No preventative maintenance logs found in that date range.");
        return;
      }
      const withDocs = await fetchPMChecks(db, clientId, selectedProperty, matches);

      const outPdf = await PDFDocument.create();
      const font = await outPdf.embedFont(StandardFonts.Helvetica);
      const boldFont = await outPdf.embedFont(StandardFonts.HelveticaBold);

      addCoverPage(outPdf, font, boldFont, withDocs, start, end);

      // Embed the template page once and reuse it as the background per room.
      const [tpl] = await outPdf.embedPdf(templateBytes, [0]);
      for (const entry of withDocs) {
        const page = outPdf.addPage([tpl.width, tpl.height]);
        page.drawPage(tpl, { x: 0, y: 0, width: tpl.width, height: tpl.height });
        fillTemplatePage(page, font, entry);
      }

      stampPageNumbersLib(outPdf, font);
      const outBytes = await outPdf.save();
      const blob = new Blob([outBytes], { type: "application/pdf" });
      downloadBlob(
        blob,
        buildReportFilename("PM_Report_Filled", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (err) {
      console.error(err);
      alert("Error generating report. See console.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        PM Report (Template)
      </Button>

      <ReportDateRangeDialog
        open={open}
        title="Generate PM Report"
        description="Stamps each room's PM responses onto the base PDF template. The template is fetched once and cached."
        busy={isGenerating}
        error={templateError}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
